import React, { useState, useEffect, useRef } from "react";
import { FiClock, FiTag, FiTool } from "react-icons/fi";
import { IoChevronDownOutline, IoCheckmarkSharp } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const API_URL = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const getAccessToken = () => {
  return (
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("token")
  );
};


export default function CreateServiceBussiness({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState(15);
  const [customDuration, setCustomDuration] = useState("");
  const [numServices, setNumServices] = useState("Unlimited");
  const [customNum, setCustomNum] = useState("");
  const [isVirtual, setIsVirtual] = useState(false);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const modalRef = useRef(null);

  const durationOptions = [
    { label: t("serviceModal.optMinutes", { count: 15 }), value: 15 },
    { label: t("serviceModal.optMinutes", { count: 30 }), value: 30 },
    { label: t("serviceModal.optHour"), value: 60 },
    { label: t("serviceModal.optHours", { count: 2 }), value: 120 },
    { label: t("serviceModal.optOther"), value: "Other" },
  ];

  const numOptions = [
    { label: "10", value: "10" },
    { label: "20", value: "20" },
    { label: t("serviceModal.optUnlimited"), value: "Unlimited" },
    { label: t("serviceModal.optOther"), value: "Other" },
  ];

  const toggleDropdown = (type) => {
    if (loading) return;
    setOpenDropdown((prev) => (prev === type ? null : type));
  };

  const handleSelect = (type, value) => {
    if (loading) return;

    if (type === "duration") {
      setDuration(value);
      if (value !== "Other") {
        setCustomDuration("");
      }
    }

    if (type === "services") {
      setNumServices(value);
      if (value !== "Other") {
        setCustomNum("");
      }
    }

    setOpenDropdown(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = t("serviceModal.errorNameRequired");
    }

    if (!price || Number.isNaN(parseFloat(price)) || parseFloat(price) <= 0) {
      newErrors.price = t("serviceModal.errorPricePositive");
    }

    if (!description.trim()) {
      newErrors.description = t("serviceModal.errorDescriptionRequired");
    }

    if (duration === "Other") {
      const parsedDuration = parseInt(customDuration, 10);
      if (!customDuration) {
        newErrors.customDuration = t("serviceModal.errorCustomDurationRequired");
      } else if (Number.isNaN(parsedDuration) || parsedDuration <= 0) {
        newErrors.customDuration = t("serviceModal.errorDurationPositive");
      }
    }

    if (numServices === "Other") {
      const parsedCustomNum = parseInt(customNum, 10);
      if (!customNum) {
        newErrors.customNum = t("serviceModal.errorCustomNumRequired");
      } else if (Number.isNaN(parsedCustomNum) || parsedCustomNum <= 0) {
        newErrors.customNum = t("serviceModal.errorNumPositive");
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error(t("serviceModal.toastFixErrors"));
      return false;
    }

    return true;
  };

  const handleCreate = async () => {
    if (loading) return;
    if (!validateForm()) return;

    const finalDuration =
      duration === "Other"
        ? parseInt(customDuration, 10) || 30
        : parseInt(duration, 10);

    const finalServiceCount =
      numServices === "Other"
        ? parseInt(customNum, 10) || 1
        : numServices === "Unlimited" || !numServices
          ? "Unlimited"
          : parseInt(numServices, 10);

    setLoading(true);

    try {
      const token = getAccessToken();

      if (!token) {
        throw new Error(t("serviceModal.errorGenericCreate"));
      }

      const payload = [
        {
          name: name.trim(),
          description: description.trim(),
          duration: finalDuration,
          time_unit: "minutes",
          price: parseFloat(price),
          currency: "USD",
          service_count: finalServiceCount,
          on_off: false,
          virtual: isVirtual,
          timeZone: getUserTimeZone(),
        },
      ];

      const response = await fetch(`${API_URL}/service/service-create/`, {
        method: "POST",
        headers: buildHeaders(),
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let responseData = null;

      try {
        responseData = responseText ? JSON.parse(responseText) : null;
      } catch {
        responseData = responseText;
      }

      if (!response.ok) {
        const serverMessage =
          typeof responseData === "string"
            ? responseData
            : responseData?.detail ||
              responseData?.message ||
              JSON.stringify(responseData);
        throw new Error(serverMessage || t("serviceModal.errorGenericCreate"));
      }

      toast.success(t("serviceModal.toastSuccess"));

      if (onCreated) {
        await Promise.resolve(onCreated(responseData));
      }

      onClose();
    } catch (error) {
      console.error("Error creating service:", error);
      toast.error(error?.message || t("serviceModal.errorGenericCreate"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (loading) return;
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [loading]);

  return (
    <AnimatePresence>
      <motion.div
        className="create-service-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => {
          if (loading) return;
          if (e.target.classList.contains("create-service-overlay")) onClose();
        }}
      >
        <motion.div
          ref={modalRef}
          className="create-service-modal"
          initial={{ opacity: 0, scale: 0.8, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 30 }}
          transition={{ type: "spring", stiffness: 150, damping: 18 }}
        >
          <div className="create-service-header">
            <button
              className="create-service-back-btn"
              onClick={onClose}
              disabled={loading}
            >
              {t("serviceModal.btnBack")}
            </button>
            <div className="create-service-title-section">
              <h2>{t("serviceModal.modalTitle")}</h2>
              <p>{t("serviceModal.modalSubtitle")}</p>
            </div>
          </div>

          <div className="create-service-form">
            <div className="create-service-input-row">
              <div className="create-service-input-group">
                <label>{t("serviceModal.labelName")}</label>
                <input
                  type="text"
                  placeholder={t("serviceModal.placeholderName")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={errors.name ? "invalid" : ""}
                  disabled={loading}
                />
                {errors.name && (
                  <span className="input-error-text">{errors.name}</span>
                )}
              </div>

              <div className="create-service-input-group create-service-dropdown">
                <label>{t("serviceModal.labelDuration")}</label>
                <div
                  className={`create-service-dropdown-field ${
                    errors.customDuration ? "invalid" : ""
                  } ${loading ? "disabled" : ""}`}
                  onClick={() => toggleDropdown("duration")}
                >
                  <FiClock className="create-service-icon" />
                  <span className="create-service-dropdown-text">
                    {duration === "Other"
                      ? `${customDuration || t("serviceModal.optCustom")} ${t("serviceModal.unitMin")}`
                      : `${duration} ${t("serviceModal.unitMin")}`}
                  </span>
                  <IoChevronDownOutline className="create-service-chevron" />
                </div>

                <AnimatePresence>
                  {openDropdown === "duration" && !loading && (
                    <motion.div
                      className="create-service-dropdown-menu"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {durationOptions.map((opt) => (
                        <div
                          key={opt.value}
                          className="create-service-dropdown-item"
                          onClick={() => handleSelect("duration", opt.value)}
                        >
                          <span>{opt.label}</span>
                          {duration === opt.value ? (
                            <div className="create-service-success-check">
                              <IoCheckmarkSharp />
                            </div>
                          ) : (
                            <div className="create-service-empty-circle"></div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {duration === "Other" && (
                  <>
                    <input
                      type="number"
                      className={`create-service-custom-input ${
                        errors.customDuration ? "invalid" : ""
                      }`}
                      placeholder={t("serviceModal.placeholderCustomDuration")}
                      value={customDuration}
                      onChange={(e) => setCustomDuration(e.target.value)}
                      disabled={loading}
                      min="1"
                    />
                    {errors.customDuration && (
                      <span className="input-error-text">
                        {errors.customDuration}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="create-service-input-row">
              <div className="create-service-input-group">
                <label>{t("serviceModal.labelPrice")}</label>
                <div
                  className={`create-service-input-icon-field ${
                    errors.price ? "invalid" : ""
                  }`}
                >
                  <FiTag className="create-service-icon" />
                  <input
                    type="number"
                    placeholder={t("serviceModal.placeholderPrice")}
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="create-service-number-input"
                    disabled={loading}
                    min="0"
                    step="0.01"
                  />
                </div>
                {errors.price && (
                  <span className="input-error-text">{errors.price}</span>
                )}
              </div>

              <div className="create-service-input-group create-service-dropdown">
                <label>{t("serviceModal.labelNumServices")}</label>
                <div
                  className={`create-service-dropdown-field ${
                    errors.customNum ? "invalid" : ""
                  } ${loading ? "disabled" : ""}`}
                  onClick={() => toggleDropdown("services")}
                >
                  <FiTool className="create-service-icon" />
                  <span className="create-service-dropdown-text">
                    {numServices === "Other"
                      ? customNum || t("serviceModal.optCustom")
                      : numServices === "Unlimited"
                        ? t("serviceModal.optUnlimited")
                        : numServices || t("serviceModal.optUnlimited")}
                  </span>
                  <IoChevronDownOutline className="create-service-chevron" />
                </div>

                <AnimatePresence>
                  {openDropdown === "services" && !loading && (
                    <motion.div
                      className="create-service-dropdown-menu"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {numOptions.map((opt) => (
                        <div
                          key={opt.value}
                          className="create-service-dropdown-item"
                          onClick={() => handleSelect("services", opt.value)}
                        >
                          <span>{opt.label}</span>
                          {(numServices || "Unlimited") === opt.value ? (
                            <div className="create-service-success-check">
                              <IoCheckmarkSharp />
                            </div>
                          ) : (
                            <div className="create-service-empty-circle"></div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {numServices === "Other" && (
                  <>
                    <input
                      type="number"
                      className={`create-service-custom-input ${
                        errors.customNum ? "invalid" : ""
                      }`}
                      placeholder={t("serviceModal.placeholderCustomNum")}
                      value={customNum}
                      onChange={(e) => setCustomNum(e.target.value)}
                      disabled={loading}
                      min="1"
                    />
                    {errors.customNum && (
                      <span className="input-error-text">
                        {errors.customNum}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="create-service-input-group create-service-textarea">
              <label>{t("serviceModal.labelDescription")}</label>
              <textarea
                placeholder={t("serviceModal.placeholderDescription")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={errors.description ? "invalid" : ""}
                disabled={loading}
              />
              {errors.description && (
                <span className="input-error-text">{errors.description}</span>
              )}
            </div>

            <div className="create-service-virtual-row">
              <div>
                <span className="create-service-virtual-label">
                  {t("serviceModal.labelVirtualService")}
                </span>
                <span className="create-service-virtual-hint">
                  {t("serviceModal.hintVirtualService")}
                </span>
              </div>
              <button
                type="button"
                className={`create-service-virtual-toggle${isVirtual ? " active" : ""}`}
                onClick={() => !loading && setIsVirtual((v) => !v)}
                aria-pressed={isVirtual}
                role="switch"
                aria-label={t("serviceModal.labelVirtualService")}
                disabled={loading}
              >
                <span className="create-service-virtual-thumb" />
              </button>
            </div>
          </div>

          <div className="create-service-footer">
            <button
              className="create-service-btn"
              onClick={handleCreate}
              disabled={loading}
            >
              {loading ? t("serviceModal.btnCreating") : t("serviceModal.btnCreate")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}